import { styled } from '@mui/material';
import React, { useState, useRef } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAccountListsV2 } from '@/dashboard/AccountListsV2/hooks/useAccountListsV2';
import { LizanoListInfo } from '@/reports/sections/ListsSection/lists/lists';
import { RapidsInput } from '@/search/index';
import { RapidsSearchData } from '@/search/rapids/rapids';
import {
  Text,
  Icons,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  useTheme,
} from '@/theme/index';
import { titleize } from '@/utils/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { ListItemActionsBar } from './ListItemActionsBar/ListItemActionsBar';
import { ListItemAllReportsTable } from './ListItemAllReportsTable/ListItemAllReportsTable';
import { ListItemEmailReportsTable } from './ListItemEmailReportsTable/ListItemEmailReportsTable';
import { ListItemPersonReportsTable } from './ListItemPersonReportsTable/ListItemPersonReportsTable';
import { ListItemPhoneReportsTable } from './ListItemPhoneReportsTable/ListItemPhoneReportsTable';
import { ListItemPropertyReportsTable } from './ListItemPropertyReportsTable/ListItemPropertyReportsTable';
import { ListItemSocialReportsTable } from './ListItemSocialReportsTable/ListItemSocialReportsTable';
import { AccountListsNullState } from '../AccountListsNullState/AccountListsNullState';

interface AccountListsListItemProps {
  list: LizanoListInfo;
  expandedListTypes?: boolean;
}

export const ALL_REPORTS: ReportType = 'ALL_REPORTS';
export const PROP_REPORTS: ReportType = 'PROPERTY_REPORTS';
export const PERSON_REPORTS: ReportType = 'PERSON_REPORTS';
export const EMAIL_REPORTS: ReportType = 'EMAIL_REPORTS';
export const PHONE_REPORTS: ReportType = 'PHONE_REPORTS';
export const SOCIAL_REPORTS: ReportType = 'SOCIAL_REPORTS';

type ReportType =
  | 'ALL_REPORTS'
  | 'PROPERTY_REPORTS'
  | 'PERSON_REPORTS'
  | 'EMAIL_REPORTS'
  | 'PHONE_REPORTS'
  | 'SOCIAL_REPORTS';

const StyledAddReportButton = styled(Button)(() => ({
  flexGrow: 1,
  width: '100%',
  fontSize: '1rem',
  letterSpacing: '0.025rem',
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  width: 'max-content',
  border: 'none',
  borderRadius: '0',
  padding: theme.spacing(1.125, 2),
  fontSize: '0.875rem',
  letterSpacing: '0.025rem',
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },
}));

const StyledBackButton = styled(Button)(() => ({
  fontSize: '0.75rem',
  letterSpacing: '0.025rem',
  textDecoration: 'underline',
  padding: 0,
  width: 'fit-content',
  minWidth: 'max-content',
}));

const AccountListsListItemTitle: React.FC<{ listName: string }> = ({
  listName,
}) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    {listName === 'saved reports' ? (
      <>
        <Icons.FavoriteBorderRounded color="primary" />
        <Text variant="h2" color="text.primary">
          Saved Reports
        </Text>
      </>
    ) : (
      <>
        <Icons.ListRounded color="primary" />
        <Text variant="h2" color="text.primary">
          {titleize(listName, { validateCaps: false })}
        </Text>
      </>
    )}
  </Stack>
);

export function AccountListsListItem({
  list,
  expandedListTypes,
}: AccountListsListItemProps) {
  const { navigate } = useAppConfigContext().routingUtils;
  const { isMobile } = useScreenSize();
  const { bulkAddListProperty } = useAccountListsV2();

  const theme = useTheme();
  const rapidsRef = useRef<HTMLDivElement>(null);

  const [
    ALL_REPORTS,
    PROP_REPORTS,
    PERSON_REPORTS,
    EMAIL_REPORTS,
    PHONE_REPORTS,
    SOCIAL_REPORTS,
  ]: ReportType[] = [
    'ALL_REPORTS',
    'PROPERTY_REPORTS',
    'PERSON_REPORTS',
    'EMAIL_REPORTS',
    'PHONE_REPORTS',
    'SOCIAL_REPORTS',
  ];
  const [viewType, setViewType] = useState<ReportType>(ALL_REPORTS);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [showRapids, setShowRapids] = useState<boolean>(false);

  const toggleShowRapids = () => {
    setShowRapids((prevState) => !prevState);
  };

  const handleAddProperty = (data: RapidsSearchData) => {
    if (list.list_code === undefined || toggleShowRapids === undefined) return;

    const { street_line, city, state, zipcode } = data;
    const property = [{ street: street_line, city, zip5: zipcode, state }];

    bulkAddListProperty.mutate(
      {
        list_code: list.list_code,
        properties: property,
      },
      {
        onSuccess: () => {
          toggleShowRapids();
        },
      }
    );
  };

  const handleRapidsButtonClick = () => {
    if (showRapids && toggleShowRapids) {
      toggleShowRapids();
    } else {
      toggleShowRapids();
      scrollToRapids();
    }
  };

  const scrollToRapids = () => {
    if (rapidsRef && rapidsRef.current) {
      rapidsRef.current!.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleViewType = (
    _event: React.MouseEvent<HTMLElement>,
    newView: string | null
  ) => {
    if (newView === null) return;
    setSelectedRows([]);
    setViewType(newView as ReportType);
  };

  const getListProperties = () => {
    return list.list_properties;
  };

  const getSelectedPropertyReportsList = () => {
    const propertyReportsList = getListProperties();

    return propertyReportsList.filter((_report, index) =>
      selectedRows.includes(index)
    );
  };

  const getAllReportsList = () => {
    return list?.list_reports?.filter((listReport) => listReport.report);
  };

  const shouldRenderAllReportsListTable = Boolean(getAllReportsList().length);
  const shouldRenderPropertyReportsListTable = Boolean(
    getListProperties().length
  );

  return (
    <Stack gap={2.5}>
      <StyledBackButton
        onClick={() => navigate('/dashboard/lists')}
        size="small"
        variant="text"
        aria-label="Go back to lists"
        startIcon={<Icons.ArrowBack />}
      >
        Back
      </StyledBackButton>
      <AccountListsListItemTitle listName={list.name} />
      {viewType === PROP_REPORTS && (
        <Stack
          direction={isMobile ? 'column-reverse' : 'row'}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          flexGrow={1}
          flexShrink={1}
          flexBasis="30%"
        >
          <ListItemActionsBar
            data={getSelectedPropertyReportsList()}
            activeList={list}
            onActionSuccess={() => setSelectedRows([])}
          />
          <StyledAddReportButton
            variant="contained"
            size="large"
            color="primary"
            aria-label="Add report to list"
            sx={(theme) => ({
              margin: isMobile ? theme.spacing(2) : theme.spacing(3),
              whiteSpace: 'nowrap',
              maxWidth: isMobile ? '100%' : '180px',
            })}
            onClick={handleRapidsButtonClick}
          >
            + Add report
          </StyledAddReportButton>
        </Stack>
      )}
      <Stack gap={1}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={viewType}
          onChange={handleViewType}
          sx={(theme) => ({
            justifyContent: 'flex-start',
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <StyledToggleButton value={ALL_REPORTS}>
            All Reports
          </StyledToggleButton>
          <StyledToggleButton value={PROP_REPORTS}>
            Property Report
          </StyledToggleButton>
          {expandedListTypes && (
            <>
              <StyledToggleButton value={PERSON_REPORTS}>
                Person Report
              </StyledToggleButton>
              <StyledToggleButton value={PHONE_REPORTS}>
                Phone Report
              </StyledToggleButton>
              <StyledToggleButton value={EMAIL_REPORTS}>
                Email Report
              </StyledToggleButton>
              <StyledToggleButton value={SOCIAL_REPORTS}>
                Social Report
              </StyledToggleButton>
            </>
          )}
        </ToggleButtonGroup>
        {viewType === ALL_REPORTS && shouldRenderAllReportsListTable && (
          <ListItemAllReportsTable reports={getAllReportsList()} />
        )}
        {viewType === ALL_REPORTS && !shouldRenderAllReportsListTable && (
          <AccountListsNullState
            noListItem={list.name !== 'saved reports'}
            handleCreateLists={() => {}}
          />
        )}
        {viewType === PROP_REPORTS && shouldRenderPropertyReportsListTable && (
          <ListItemPropertyReportsTable
            listProperties={getListProperties()}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            showRapids={showRapids}
            toggleShowRapids={toggleShowRapids}
            viewType={viewType}
            listCode={list.list_code}
            rapidsRef={rapidsRef}
          />
        )}
        {viewType === PERSON_REPORTS && expandedListTypes && (
          <ListItemPersonReportsTable
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        )}
        {viewType === PHONE_REPORTS && expandedListTypes && (
          <ListItemPhoneReportsTable
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        )}
        {viewType === EMAIL_REPORTS && expandedListTypes && (
          <ListItemEmailReportsTable
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        )}
        {viewType === SOCIAL_REPORTS && expandedListTypes && (
          <ListItemSocialReportsTable
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        )}
        {showRapids && viewType === PROP_REPORTS && (
          <>
            <div ref={rapidsRef} />
            <RapidsInput
              parentFormSubmitHandler={handleAddProperty}
              addressOnly
              inputOptions={{
                label: '',
                placeholder: 'Enter an address, city, or zip code',
                inputWidth: { xs: '100%', md: '100%' },
                hideSearchButton: true,
                customStyles: {
                  marginTop: theme.spacing(3),
                },
              }}
            />
          </>
        )}
        {viewType === PROP_REPORTS && !shouldRenderPropertyReportsListTable && (
          <AccountListsNullState
            noListItem={list.name !== 'saved reports'}
            handleCreateLists={() => {}}
          />
        )}
        {viewType === PROP_REPORTS && (
          <Button
            sx={{ maxWidth: '120px', marginTop: theme.spacing(3) }}
            onClick={toggleShowRapids}
          >
            <Icons.Add />
            <Text sx={{ whiteSpace: 'nowrap' }}>Add report</Text>
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
